import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Volume2, VolumeX } from "lucide-react";
import { useState, useRef, useEffect } from "react";

export const HeroSection = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  const heroVideoSrc =
    process.env.REACT_APP_HERO_VIDEO_URL ||
    (isMobile ? "/eagle_video_mobile.mp4" : "/eagle_video.mp4");
  const [isMuted, setIsMuted] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.6], [0.1, 0.5]);

  const handleScrollDown = () => {
    const el = document.querySelector("#about");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <section ref={sectionRef} id="hero" data-testid="hero-section" className="relative min-h-screen flex items-end justify-center overflow-hidden pb-28">
      <motion.div className="absolute inset-0" style={{ scale: videoScale }}>
        {videoError || !heroVideoSrc ? (
          <img
            src="/hero_thumbnail.jpg"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            data-testid="hero-image-fallback"
          />
        ) : (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            poster="/hero_thumbnail.jpg"
            data-testid="hero-video"
            className="w-full h-full object-cover"
            onError={() => setVideoError(true)}
          >
            <source src={heroVideoSrc} type="video/mp4" />
          </video>
        )}
        <motion.div className="absolute inset-0 bg-black" style={{ opacity: overlayOpacity }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FAFAF7] via-black/20 to-black/10" />
      </motion.div>

      {!videoError && (
        <motion.button onClick={toggleMute} data-testid="hero-mute-toggle" initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.5, type: "spring", stiffness: 200 }} className="absolute top-24 right-6 sm:right-8 z-20 w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white/70 hover:text-white hover:border-white/50 transition-all duration-300 backdrop-blur-sm bg-black/20">
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </motion.button>
      )}

      <motion.div style={{ y: textY, opacity: textOpacity }} className="relative z-20 text-center">
        <motion.p
          initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="text-base sm:text-lg text-white mb-6 tracking-wide drop-shadow-lg"
          style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
          data-testid="hero-tagline"
        >
          Personalised Gifting Solution, Since 1976
        </motion.p>
        <motion.a
          href="#products"
          data-testid="hero-cta-button"
          onClick={(e) => { e.preventDefault(); document.querySelector("#products")?.scrollIntoView({ behavior: "smooth" }); }}
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(122,26,49,0.3)" }}
          whileTap={{ scale: 0.97 }}
          className="inline-block bg-[#7A1A31] text-white px-10 py-4 rounded-full hover:bg-[#5C162E] transition-colors duration-300 text-sm font-medium tracking-widest uppercase shadow-lg shadow-[#7A1A31]/20"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Explore Products
        </motion.a>
      </motion.div>

      <motion.button onClick={handleScrollDown} data-testid="hero-scroll-down" initial={{ opacity: 0 }} animate={{ opacity: 1, y: [0, 8, 0] }} transition={{ opacity: { delay: 1.2 }, y: { repeat: Infinity, duration: 2 } }} className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#7A1A31]/60 hover:text-[#7A1A31] transition-colors z-20">
        <ChevronDown className="w-7 h-7" />
      </motion.button>
    </section>
  );
};
